/**
 * Molecule ED Assessment engine
 * State, navigation, validation, local persistence, redirect on finish (no API)
 */
(function () {
  'use strict';

  var config = null;
  var root = null;
  var state = {
    pageIndex: 0,
    answers: {},
    history: [],
    pageError: '',
    submitError: '',
    submitSuccess: '',
    submitting: false
  };

  function UI() {
    return window.WmEdAssessmentUI;
  }

  function mergeOverrides() {
    var o = window.__wmEdAssessmentOverrides || {};
    config = Object.assign({}, window.wmEdAssessmentConfig || {});
    if (o.successRedirectUrl) config.successRedirectUrl = o.successRedirectUrl;
    if (typeof o.disableSuccessRedirect === 'boolean') {
      config.disableSuccessRedirect = o.disableSuccessRedirect;
    }
    if (typeof o.designMode === 'boolean') {
      config.designMode = o.designMode;
    }
    if (typeof o.successRedirectDelayMs === 'number') {
      config.successRedirectDelayMs = o.successRedirectDelayMs;
    }
    if (o.clinicianBookingUrl) config.clinicianBookingUrl = o.clinicianBookingUrl;
    if (o.homeUrl) config.homeUrl = o.homeUrl;
    if (o.formType) config.formType = o.formType;
    if (o.api) config.api = Object.assign({}, config.api || {}, o.api);
    if (config.api && o.formType) config.api.formType = o.formType;
  }

  function storageKey() {
    return (config && config.storageKey) || 'webmed_ed_assessment';
  }

  function save() {
    try {
      localStorage.setItem(
        storageKey(),
        JSON.stringify({
          pageIndex: state.pageIndex,
          answers: state.answers || {},
          history: state.history || [],
          formType: (config && config.formType) || 'webmed_ed',
          savedAt: Date.now()
        })
      );
    } catch (e) {}
  }

  function clearStorage() {
    try {
      localStorage.removeItem(storageKey());
    } catch (e) {}
  }

  function load() {
    try {
      var raw = localStorage.getItem(storageKey());
      if (!raw) return;
      var parsed = JSON.parse(raw);
      var ttl = (config && config.storageTtlMs) || 20 * 60 * 1000;
      if (!parsed || !parsed.savedAt || Date.now() - parsed.savedAt > ttl) {
        clearStorage();
        return;
      }
      state.pageIndex = parsed.pageIndex || 0;
      state.answers = parsed.answers || {};
      state.history = parsed.history || [];
    } catch (e) {
      clearStorage();
    }
  }

  function getPage() {
    return config.phases[state.pageIndex];
  }

  function findPhaseIndex(id) {
    for (var i = 0; i < config.phases.length; i++) {
      if (config.phases[i].id === id) return i;
    }
    return -1;
  }

  function matchesShowIf(rule) {
    if (!rule) return true;
    if (Array.isArray(rule)) {
      for (var i = 0; i < rule.length; i++) {
        if (!matchesShowIf(rule[i])) return false;
      }
      return true;
    }
    var val = state.answers[rule.field];
    if (rule.equals !== undefined) return val === rule.equals;
    if (rule.notEquals !== undefined) return val !== rule.notEquals;
    if (rule.in) return rule.in.indexOf(val) !== -1;
    return true;
  }

  function isPhaseVisible(phase) {
    if (!phase) return false;
    if (phase.routeOnly) return false;
    return matchesShowIf(phase.showIf);
  }

  function nextLinearIndex(from) {
    for (var i = from + 1; i < config.phases.length; i++) {
      if (isPhaseVisible(config.phases[i])) return i;
    }
    return -1;
  }

  /** Progress from step position: first question = 0%, last flow step = 100%. */
  function computeProgress(pageIndex) {
    if (!config || !config.phases || !config.phases.length) return 0;
    var flow = [];
    for (var i = 0; i < config.phases.length; i++) {
      if (!config.phases[i].routeOnly) flow.push(i);
    }
    if (!flow.length) return 0;

    var idx = pageIndex;
    var page = config.phases[idx];
    if (page && page.routeOnly && state.history && state.history.length) {
      idx = state.history[state.history.length - 1];
    }

    var step = flow.indexOf(idx);
    if (step < 0) {
      for (var j = 0; j < flow.length; j++) {
        if (flow[j] <= idx) step = j;
      }
    }
    if (step < 0) step = 0;
    if (flow.length === 1) return 0;
    return Math.round((step / (flow.length - 1)) * 100);
  }

  function goToPhaseId(id) {
    var idx = findPhaseIndex(id);
    if (idx < 0) return;
    state.history.push(state.pageIndex);
    state.pageIndex = idx;
    state.pageError = '';
    save();
    render();
  }

  function finishRedirect() {
    save();
    var Api = window.WmEdAssessmentApi;
    var url =
      Api && Api.buildSelectProductUrl
        ? Api.buildSelectProductUrl(config, state)
        : (config && config.successRedirectUrl) || '/pages/select-product';
    window.location.href = url;
  }

  function finishSubmit() {
    if (state.submitting || state.submitSuccess) return;
    if (!window.WmEdAssessmentApi || !window.WmEdAssessmentApi.submitAssessment) {
      state.submitSuccess =
        'Your assessment has been submitted successfully.';
      render();
      return;
    }
    state.submitting = true;
    state.pageError = '';
    state.submitError = '';
    state.submitSuccess = '';
    render();
    window.WmEdAssessmentApi.submitAssessment(state, config)
      .then(function () {
        state.submitting = false;
        if (window.WmClEvents) {
          window.WmClEvents.track(
            'ed_complete_assessment',
            Object.assign({ funnel: 'ed' }, window.WmClEvents.identityFromAnswers(state.answers)),
            { once: true }
          );
        }
        clearStorage();
        if (config.disableSuccessRedirect) {
          state.submitSuccess =
            'Your assessment has been submitted successfully.';
          render();
          return;
        }
        state.submitSuccess =
          'Your assessment has been submitted successfully. Redirecting you shortly…';
        render();
        var delay =
          typeof config.successRedirectDelayMs === 'number'
            ? config.successRedirectDelayMs
            : 4000;
        window.setTimeout(function () {
          finishRedirect();
        }, delay);
      })
      .catch(function (err) {
        state.submitting = false;
        state.submitError =
          (err && err.message) || 'Unable to submit assessment. Please try again.';
        render();
      });
  }

  function bookClinician() {
    var url = (config && config.clinicianBookingUrl) || '/pages/book-a-consultation';
    window.location.href = url;
  }

  function goHome() {
    var url = (config && config.homeUrl) || '/';
    window.location.href = url;
  }

  /** Answer / API storage key for a phase (edit `apiKey` in config). */
  function pageApiKey(page) {
    return (page && (page.apiKey || page.field)) || '';
  }

  function ackApiKey(page) {
    return (page && (page.ackApiKey || page.ackField)) || '';
  }

  function checkboxApiKey(c) {
    return (c && (c.apiKey || c.field)) || '';
  }

  function otherFieldKey(pageOrKey) {
    if (pageOrKey && typeof pageOrKey === 'object') {
      if (pageOrKey.otherApiKey) return pageOrKey.otherApiKey;
      var k = pageApiKey(pageOrKey);
      return k ? k + '_other' : '';
    }
    return pageOrKey ? pageOrKey + '_other' : '';
  }

  /** True when Other is selected but the free-text field is still empty. */
  function otherTextMissing(page) {
    if (!page || !page.otherValue) return false;
    var key = pageApiKey(page);
    var a = state.answers;
    var selected = a[key];
    var hasOther = Array.isArray(selected)
      ? selected.indexOf(page.otherValue) !== -1
      : selected === page.otherValue;
    if (!hasOther) return false;
    return !(a[otherFieldKey(page)] || '').trim();
  }

  function isValidEmail(v) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || '').trim());
  }

  function continueDisabled(page) {
    if (!page) return true;
    if (state.submitting || state.submitSuccess) return true;
    var a = state.answers;
    var key = pageApiKey(page);
    if (page.type === 'details') {
      return (
        !(a.first_name || '').trim() ||
        !(a.last_name || '').trim() ||
        !(a.phone || '').trim() ||
        !isValidEmail(a.email)
      );
    }
    if (page.type === 'single') {
      return !a[key] || otherTextMissing(page);
    }
    if (page.type === 'multiselect') {
      var list = a[key];
      if (!Array.isArray(list) || !list.length) return true;
      return otherTextMissing(page);
    }
    if (page.type === 'preference') {
      var ack = ackApiKey(page);
      return !a[key] || (ack && !a[ack]);
    }
    if (page.type === 'infoAck') return !a[key];
    if (page.type === 'dualConfirm') {
      var checks = page.checkboxes || [];
      for (var i = 0; i < checks.length; i++) {
        if (!a[checkboxApiKey(checks[i])]) return true;
      }
      return false;
    }
    return false;
  }

  function syncContinueButton() {
    if (!root) return;
    var btn = root.querySelector('[data-action="next"]');
    if (!btn) return;
    var disabled = continueDisabled(getPage());
    btn.disabled = disabled;
    if (disabled) {
      btn.setAttribute('aria-disabled', 'true');
      btn.classList.add('is-disabled');
    } else {
      btn.removeAttribute('aria-disabled');
      btn.classList.remove('is-disabled');
    }
  }

  function validate(page) {
    if (!page) return 'Something went wrong.';
    var a = state.answers;
    var key = pageApiKey(page);
    if (page.type === 'details') {
      if (!(a.first_name || '').trim()) return 'Please enter your first name.';
      if (!(a.last_name || '').trim()) return 'Please enter your last name.';
      if (!(a.phone || '').trim()) return 'Please enter your phone number.';
      if (!isValidEmail(a.email)) return 'Please enter a valid email address.';
      return '';
    }
    if (page.type === 'single' || page.type === 'preference') {
      if (!a[key]) return 'Please select an option to continue.';
      var ack = ackApiKey(page);
      if (page.type === 'preference' && ack && !a[ack]) {
        return 'Please confirm the product claims notice to continue.';
      }
      return '';
    }
    if (page.type === 'multiselect') {
      var list = a[key];
      if (!Array.isArray(list) || !list.length) {
        return 'Please select at least one option to continue.';
      }
      if (page.otherValue && list.indexOf(page.otherValue) !== -1) {
        var other = (a[otherFieldKey(page)] || '').trim();
        if (!other) return 'Please tell us more about “Other”.';
      }
      return '';
    }
    if (page.type === 'infoAck') {
      if (!a[key]) return 'Please confirm to continue.';
      return '';
    }
    if (page.type === 'dualConfirm') {
      var checks = page.checkboxes || [];
      for (var i = 0; i < checks.length; i++) {
        if (!a[checkboxApiKey(checks[i])]) {
          return 'Please confirm all statements to continue.';
        }
      }
      return '';
    }
    return '';
  }

  function exclusiveListFor(page) {
    if (!page) return [];
    if (Array.isArray(page.exclusiveValues) && page.exclusiveValues.length) {
      return page.exclusiveValues.slice();
    }
    if (page.exclusiveValue) return [page.exclusiveValue];
    return [];
  }

  function setMultiValue(field, value, page) {
    var cur = Array.isArray(state.answers[field]) ? state.answers[field].slice() : [];
    var idx = cur.indexOf(value);
    var selecting = idx === -1;
    var exclusiveList = exclusiveListFor(page);
    var otherKey = page ? otherFieldKey(page) : '';

    if (selecting) cur.push(value);
    else cur.splice(idx, 1);

    if (exclusiveList.length) {
      var isExclusive = exclusiveList.indexOf(value) !== -1;
      if (selecting && isExclusive) {
        // Selecting None / Other clears every other option
        cur = [value];
        if (otherKey && page.otherValue && exclusiveList.indexOf(page.otherValue) !== -1) {
          if (value !== page.otherValue) state.answers[otherKey] = '';
        }
      } else if (selecting) {
        // Selecting a normal option clears exclusive ones (None / Other)
        cur = cur.filter(function (v) {
          return exclusiveList.indexOf(v) === -1;
        });
        if (otherKey && page.otherValue && exclusiveList.indexOf(page.otherValue) !== -1) {
          state.answers[otherKey] = '';
        }
      } else if (!selecting && page && value === page.otherValue && otherKey) {
        state.answers[otherKey] = '';
      }
    }

    state.answers[field] = cur;
  }

  function selectedOption(page) {
    if (!page || !page.options) return null;
    var val = state.answers[pageApiKey(page)];
    for (var i = 0; i < page.options.length; i++) {
      if (page.options[i].value === val) return page.options[i];
    }
    return null;
  }

  function goNext() {
    var page = getPage();
    if (!page) return;
    if (state.submitSuccess) return;
    var err = validate(page);
    if (err) {
      state.pageError = err;
      render();
      return;
    }
    state.pageError = '';
    state.submitError = '';

    if (page.type === 'details') {
      loadQuizPlpProfile();
      if (window.WmClEvents) {
        window.WmClEvents.track(
          'ed_start_assessment',
          Object.assign({ funnel: 'ed' }, window.WmClEvents.identityFromAnswers(state.answers))
        );
      }
    }

    if (page.finish) {
      finishSubmit();
      return;
    }

    var opt = selectedOption(page);
    if (opt && opt.nextPhaseId) {
      goToPhaseId(opt.nextPhaseId);
      return;
    }
    if (page.nextPhaseId) {
      goToPhaseId(page.nextPhaseId);
      return;
    }

    var next = nextLinearIndex(state.pageIndex);
    if (next < 0) {
      finishSubmit();
      return;
    }
    state.history.push(state.pageIndex);
    state.pageIndex = next;
    save();
    render();
  }

  function goBack() {
    if (!state.history.length) return;
    state.pageIndex = state.history.pop();
    state.pageError = '';
    save();
    render();
  }

  function renderInner(page) {
    var ui = UI();
    var a = state.answers;
    var html = '';

    if (page.type === 'kickout') {
      return ui.kickoutScreen(page);
    }
    if (page.type === 'details') {
      html = ui.details(page, a, { disabled: continueDisabled(page) });
    } else if (page.type === 'intro') {
      html = ui.intro(page);
    } else if (page.type === 'infoAck') {
      html = ui.infoAck(page, a);
    } else if (page.type === 'dualConfirm') {
      html = ui.dualConfirm(page, a);
    } else if (page.type === 'single' || page.type === 'multiselect') {
      var key = pageApiKey(page);
      var q = Object.assign({}, page, { apiKey: key, field: key });
      if (page.otherValue) {
        q._otherValue = a[otherFieldKey(page)] || '';
        q.otherApiKey = otherFieldKey(page);
      }
      html = ui.optionCards(q, a[key], page.type === 'multiselect');
      html += ui.primaryButton(
        state.submitting && page.finish ? 'Submitting…' : page.nextLabel || 'Continue',
        'next',
        {
          disabled: continueDisabled(page)
        }
      );
      if (page.finish) {
        if (state.submitError) html += ui.alert(state.submitError, 'error');
        if (state.submitSuccess) html += ui.alert(state.submitSuccess, 'success');
      }
    } else if (page.type === 'preference') {
      var pKey = pageApiKey(page);
      var ack = ackApiKey(page);
      var pq = Object.assign({}, page, { apiKey: pKey, field: pKey });
      html = ui.optionCards(pq, a[pKey], false);
      if (ack) {
        html += ui.confirmToggle(ack, !!a[ack], page.ackLabel || 'I confirm');
      }
      html += ui.primaryButton(
        state.submitting ? 'Submitting…' : page.nextLabel || 'Continue',
        'next',
        {
          disabled: continueDisabled(page)
        }
      );
    } else {
      html = ui.alert('Unknown step type');
    }

    if (state.pageError) html = ui.alert(state.pageError, 'error') + html;
    return html;
  }

  function render() {
    if (!root || !config) return;
    var page = getPage();
    if (!page) {
      root.innerHTML = UI().alert('Assessment configuration error');
      return;
    }

    var showBack = page.showBack !== false && state.history.length > 0 && !page.hideChrome;
    if (page.hideChrome) showBack = false;

    // Animate only when navigating to a different step (not option toggles)
    var animate = state._lastRenderedPageIndex !== state.pageIndex;
    state._lastRenderedPageIndex = state.pageIndex;

    root.innerHTML = UI().shell({
      progress: computeProgress(state.pageIndex),
      showBack: showBack,
      hideChrome: !!page.hideChrome,
      animate: animate,
      inner: renderInner(page)
    });

    if (animate) {
      var slide = root.querySelector('[data-slide]');
      if (slide) {
        requestAnimationFrame(function () {
          slide.classList.add('wmaed__slide-active');
        });
      }
    }

    bindEvents();
  }

  function bindEvents() {
    if (!root) return;

    root.onclick = function (e) {
      var t = e.target.closest('[data-action]');
      if (t) {
        var action = t.getAttribute('data-action');
        if (action === 'back') {
          e.preventDefault();
          if (state.submitSuccess) return;
          goBack();
          return;
        }
        if (action === 'next') {
          e.preventDefault();
          goNext();
          return;
        }
        if (action === 'book-clinician') {
          e.preventDefault();
          bookClinician();
          return;
        }
        if (action === 'go-home') {
          e.preventDefault();
          goHome();
          return;
        }
      }

      var opt = e.target.closest('[data-option-field]');
      if (opt) {
        e.preventDefault();
        if (state.submitSuccess || state.submitting) return;
        var field = opt.getAttribute('data-option-field');
        var value = opt.getAttribute('data-option-value');
        var multi = opt.getAttribute('data-option-multi') === '1';
        var page = getPage();
        if (multi) {
          setMultiValue(field, value, page);
        } else {
          state.answers[field] = value;
        }
        state.pageError = '';
        state.submitError = '';
        save();
        render();
        return;
      }

      var confirm = e.target.closest('.wmaed__confirm');
      if (confirm) {
        if (state.submitSuccess || state.submitting) return;
        var input = confirm.querySelector('input[data-field]');
        if (input) {
          var f = input.getAttribute('data-field');
          state.answers[f] = !state.answers[f];
          state.pageError = '';
          state.submitError = '';
          save();
          render();
        }
      }
    };

    root.oninput = function (e) {
      var el = e.target;
      if (!el) return;
      if (el.getAttribute('data-other-key')) {
        state.answers[el.getAttribute('data-other-key')] = el.value;
        state.pageError = '';
        state.submitError = '';
        save();
        syncContinueButton();
        return;
      }
      if (el.getAttribute('data-field') && !el.getAttribute('data-confirm')) {
        var field = el.getAttribute('data-field');
        if (el.type === 'checkbox' || el.type === 'radio') return;
        state.answers[field] = el.value;
        state.pageError = '';
        state.submitError = '';
        save();
        syncContinueButton();
      }
    };
  }

  function applyWebsiteCustomerFallbacks() {
    var o = window.__wmEdAssessmentOverrides || {};
    // Prefill from logged-in Shopify customer only (no URL params)
    if (o.customerEmailFallback && !state.answers.email) {
      state.answers.email = o.customerEmailFallback;
    }
    if (o.customerFirstNameFallback && !state.answers.first_name) {
      state.answers.first_name = o.customerFirstNameFallback;
    }
    if (o.customerLastNameFallback && !state.answers.last_name) {
      state.answers.last_name = o.customerLastNameFallback;
    }
    if (o.customerPhoneFallback && !state.answers.phone) {
      state.answers.phone = o.customerPhoneFallback;
    }
  }

  function loadQuizPlpProfile() {
    var Api = window.WmEdAssessmentApi;
    var email = (state.answers && state.answers.email) || '';
    if (!email || !Api || !Api.fetchQuizPlp) {
      return Promise.resolve(null);
    }
    return Api.fetchQuizPlp(email, config)
      .then(function (body) {
        var data = (body && body.data) || {};
        Api.applyQuizPlpToAnswers(state.answers, data);
        state.quizPlp = data;
        try {
          localStorage.setItem(storageKey() + '_quiz_plp', JSON.stringify(data));
        } catch (e) {}
        save();
        return data;
      })
      .catch(function (err) {
        console.warn('[wm-ed-assessment] quiz-plp failed', err);
        return null;
      });
  }

  function init() {
    root = document.getElementById('wm-ed-assessment-app');
    if (!root) return;
    mergeOverrides();
    if (!config || !config.phases || !config.phases.length) {
      root.innerHTML = '<p>ED assessment configuration missing.</p>';
      return;
    }

    clearStorage();
    state = {
      pageIndex: 0,
      answers: { form_type: (config.api && config.api.formType) || config.formType || 'webmed_ed' },
      history: [],
      pageError: '',
      submitError: '',
      submitSuccess: '',
      submitting: false,
      quizPlp: null
    };
    applyWebsiteCustomerFallbacks();
    render();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.__wmEdAssessmentTest = {
    getState: function () {
      return state;
    },
    getConfig: function () {
      return config;
    }
  };
})();
